import React, { useState, useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Snackbar, SnackbarContent } from "@mui/material";
import Agent from "../../../Network/Agent";
import { handleSnackbarClose, objectsEqual } from "../../../Components/Utils/utilities";
import ToolBar from "../../../Components/Elements/ToolBar";
import ButtonPrimary from "../../../Components/Elements/ButtonPrimary";
import { useNavigate } from "react-router-dom";
import { getStoreName } from "../../../Components/Utils/getStoreName";
import {useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import { Spinner } from "react-bootstrap";
import SpinnerCustom from "../../../Components/ui/SpinnerCustom";

const ProductListSorting = () => {
    const storeUsername = getStoreName();

    const [products, setProducts] = useState([]);
    const [showSubmitBtn, setShowSubmitBtn] = useState(false);
    const [submitBtnIsLoading, setSubmitBtnIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const navigate = useNavigate();

    const queryClient = useQueryClient()

    const vertical = "bottom";
    const horizontal = "center";

    // Infinite Query for Paginated Product Fetching
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
    } = useInfiniteQuery({
        queryKey: ['productsSorting', storeUsername],
        queryFn: ({ pageParam = 1 }) =>
            Agent.Product.getListForQuickEdit('all', storeUsername, pageParam).then(res => ({
                products: res.data.result.products,
                hasMore: res.data.meta.hasMore,
            })),
        getNextPageParam: (lastPage, allPages) => lastPage.hasMore ? allPages.length + 1 : undefined,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Merge pages of products into a single array
    useEffect(() => {
        if (data) {
            const newProducts = data.pages.flatMap(page => page.products);
            setProducts(newProducts);
        }
    }, [data]);

    // Handle drag and drop
    const handleOnDragEnd = result => {
        if (!result.destination) return;
        const newArray = Array.from(products);
        const [draggedItem] = newArray.splice(result.source.index, 1);
        newArray.splice(result.destination.index, 0, draggedItem);
        if (!objectsEqual(newArray, products)) {
            setShowSubmitBtn(true);
            setProducts(newArray);
        }
    };

    // Handle submit button click
    const handleSubmitBTN = () => {
        setSubmitBtnIsLoading(true);
        const finalList = products.map((product, index) => ({
            sort: index,
            code: product.code,
        }));

        Agent.Product.updateSorting(finalList)
            .then(response => {
                setSnackbarMessage(response.data.message);
                setSnackbarOpen(true);
                setSubmitBtnIsLoading(false);
                setShowSubmitBtn(false);
                queryClient.invalidateQueries({ queryKey: ['products'] })
                queryClient.invalidateQueries({ queryKey: ['productsSorting', storeUsername] });
            })
            .catch(error => {
                setSubmitBtnIsLoading(false);
                setShowSubmitBtn(false);
                console.log(error.response);
            });
    };

    // Infinite Scroll event handler
    const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
        const threshold = 1000;

        if (scrollHeight - scrollTop <= clientHeight + threshold && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    // Attach and detach scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasNextPage, isFetchingNextPage]);

    if (isFetching && products.length === 0) {
        return (
            <SpinnerCustom className="pt-20">
                <Spinner
                    className="custom-spinner d-inline-block"
                    style={{ color: '#000', width: '36px', height: '36px' }}
                    animation="border"
                />
            </SpinnerCustom>
        );
    }

    return (
        <div className="pb-64">
            <ToolBar
                customClasses="box-shadow"
                title="محصولات"
                navigate={() => navigate(-1)}
            >
                {showSubmitBtn && (
                    <div onClick={handleSubmitBTN}>
                        <ButtonPrimary
                            mode="light-mode"
                            spinnerClasses="cl-blue"
                            isLoading={submitBtnIsLoading}
                        >
                            <div className="mobile-h4 desktop-h4 cl-blue">
                                تایید
                            </div>
                        </ButtonPrimary>
                    </div>
                )}
            </ToolBar>
            <div className="container mt-20">
                <div className="row">
                    <div className="col-12">
                        <div className="mobile-body-1-regular cl-subtitle text-center">
                            روی هر آیتم نگه دارید و سپس آن را جابجا کنید.
                        </div>
                    </div>
                </div>
            </div>
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <div className="container mt-3">
                    <Droppable droppableId="categories">
                        {provided => (
                            <div className="row" ref={provided.innerRef} {...provided.droppableProps}>
                                {products.length > 0 ? (
                                    products.map(({ code, title, status }, index) => (
                                        <Draggable key={code} draggableId={code} index={index}>
                                            {provided => (
                                                <div
                                                    className="col-12"
                                                    ref={provided.innerRef}
                                                    {...provided.dragHandleProps}
                                                    {...provided.draggableProps}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center p-20 border bg-white">
                                                        <div className="mobile-h5 cl-black">{title}</div>
                                                        <div className="d-flex align-items-center">
                                                            {status === 'active' ? (
                                                                <div className="mobile-body-1-strong cl-grass">فعال</div>
                                                            ) : (
                                                                <div className="mobile-body-1-strong cl-tomato">غیر فعال</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))
                                ) : (
                                    <div className="text-center">
                                        <p>No products available</p>
                                    </div>
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            </DragDropContext>
            {isFetchingNextPage && (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            )}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => {
                    handleSnackbarClose();
                    setSnackbarOpen(false);
                }}
                anchorOrigin={{ vertical, horizontal }}
                key={vertical + horizontal}
            >
                <SnackbarContent
                    className="dir-rtl font-family-dana"
                    message={snackbarMessage}
                />
            </Snackbar>
        </div>
    );
};

export default ProductListSorting;
